import React, { useState, useEffect, useCallback } from 'react';
import { IMG } from '../../data/homeData';
import { useReddyAuth, DEMO_USER } from '../home/ReddyAuth';
import { API_ENDPOINTS } from '../../config/api';

/* Shared login design (the reference ".login-block": left login form + right promo
   carousel), used by BOTH the header LoginModal (popup) and the /login page.
   Does the REAL backend login (POST /api/auth/login) — stores reddyannabook_user +
   token, updates ReddyAuth so the header flips to logged-in, fires the user-logged-in
   event for the legacy header, then calls onSuccess (modal → close, page → navigate).
   "Login with Demo ID" keeps the no-backend demo session for browsing. */

/* real login promos are live-server-only → primary src attempted, falls back to the
   promos already copied into /images/reddy/. */
const SLIDES = [
  { real: `${IMG}/log_one.png`, fb: `${IMG}/lgin-depoist-withdraw-image.png`, alt: 'Fast Deposit / Withdrawal' },
  { real: `${IMG}/log_three.png`, fb: `${IMG}/login-online-casino.png`, alt: 'Online Casino' },
  { real: `${IMG}/log_four.png`, fb: `${IMG}/login-sports-book.png`, alt: 'Sports Book' },
];

export default function LoginCard({ onSuccess, onClose, setUser }) {
  const { login } = useReddyAuth();

  // promo carousel
  const [i, setI] = useState(0);
  const n = SLIDES.length;
  const next = useCallback(() => setI((p) => (p + 1) % n), [n]);
  const prev = () => setI((p) => (p - 1 + n) % n);
  useEffect(() => { const t = setInterval(next, 4000); return () => clearInterval(t); }, [next]);

  // form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const stop = (e) => e.preventDefault();

  /* REAL login — copied verbatim from the old LoginPage flow. */
  const onSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    if (!username.trim()) { setError('Please enter your username'); return; }
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      let data;
      try { data = await response.json(); }
      catch (_) { setError('Invalid response from server. Please try again.'); setLoading(false); return; }

      if (response.ok) {
        const u = data.data?.user ?? data.user;
        const token = data.data?.token ?? data.token;
        if (u) {
          const mustChange = data.data?.must_change_password ?? data.must_change_password;
          if (mustChange) localStorage.setItem('must_change_password', 'true');
          else localStorage.removeItem('must_change_password');
          try {
            localStorage.setItem('reddyannabook_user', JSON.stringify(u));
            if (token) localStorage.setItem('reddyannabook_token', token);
          } catch (_) {}
          if (setUser) setUser(u);                                   // keep App-level state in sync (page passes it)
          login(u);                                                  // flip the reddy header to logged-in
          window.dispatchEvent(new CustomEvent('user-logged-in'));   // notify the legacy header
          if (mustChange) { window.location.href = '/force-change-password'; return; }
          if (onSuccess) onSuccess();
        } else {
          setError('Login succeeded but no user data received.');
        }
      } else if (response.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError(data.message || data.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  /* demo — no backend; flips the header to a demo session for browsing */
  const demoLogin = (e) => { if (e) e.preventDefault(); login(DEMO_USER); if (onSuccess) onSuccess(); };

  return (
    <section className="login-block">
      {onClose && (
        <button type="button" aria-label="Close" className="close pull-right" onClick={onClose}>
          <span aria-hidden="true">&times;</span>
        </button>
      )}
      <div className="container">
        <div className="row align-items-center">
          {/* LEFT — login form */}
          <div className="col-md-6 login-sec">
            <h2 className="text-center">Login Now</h2>
            {error && <div className="login-error" role="alert">{error}</div>}
            <form className="login-form" onSubmit={onSubmit}>
              <div className="form-group">
                <label htmlFor="li-user" className="text-uppercase">Username / Mobile Number</label>
                <input id="li-user" type="text" className="form-control" value={username}
                  onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
              </div>
              <div className="form-group">
                <label htmlFor="li-pass" className="text-uppercase">Password</label>
                <input id="li-pass" type="password" className="form-control" value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
              </div>
              <div className="form-check">
                <label className="form-check-label">
                  <input type="checkbox" className="form-check-input" />
                  <span>Remember Me?</span>
                </label>
              </div>
              <div className="form-group text-center">
                <a href="/forgot-password" className="fp">Forgot Password/Username?</a>
              </div>
              <button type="submit" className="btn button-login btn-login" disabled={loading}>
                {loading ? 'Logging in…' : 'Log In'}
              </button>
              <button type="button" className="btn button-login btn-login" onClick={demoLogin}>Login with Demo ID</button>
            </form>
            <div className="copy-text">Powered by <a href="/" onClick={stop}>Reddy Anna Book</a>
              <p><a href="mailto:support@reddyannabook.me">support@reddyannabook.me</a></p>
            </div>
            <div className="text-center mb-1">
              <a target="_blank" rel="noreferrer" className="btn btn-success w-100" href="/" onClick={stop}>
                <i className="bi bi-android2" /><span>Download .Apk</span><i className="bi bi-download fs-5 ms-1" />
              </a>
            </div>
            <div className="social_connect p-0 mt-0 mb-1">
              <a target="_blank" rel="noreferrer" className="btn d-block font-share purple_bg text-center text-light m-0" href="/" onClick={stop}>
                <i className="bi bi-share" /><span>Share This Apk to Friend</span><i className="bi bi-chevron-right fs-5 ms-1" />
              </a>
            </div>
          </div>
          {/* RIGHT — promo carousel */}
          <div className="col-md-6 banner-sec d-none d-sm-block">
            <div className="banner-sec-content">
              <div className="carousel slide">
                <div className="carousel-inner">
                  {SLIDES.map((s, idx) => (
                    <div className={`carousel-item${idx === i ? ' active' : ''}`} key={idx}>
                      <img src={s.real} className="img-fluid" alt={s.alt}
                        onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = '1'; e.currentTarget.src = s.fb; } }} />
                    </div>
                  ))}
                </div>
                <button type="button" className="carousel-control-prev" onClick={prev}>
                  <span aria-hidden="true" className="carousel-control-prev-icon" />
                  <span className="visually-hidden">Previous</span>
                </button>
                <button type="button" className="carousel-control-next" onClick={next}>
                  <span aria-hidden="true" className="carousel-control-next-icon" />
                  <span className="visually-hidden">Next</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
